import { use, useId, useMemo, useRef } from "react";
import { colorizePattern } from "regex-colorizer";

import { CanvasTextArea } from "@/components/canvas-editor/components/Inputs";
import {
  CustomNodeEditorContext,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { Textarea } from "@/components/ui/textarea";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface CanvasRegexTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  isReadOnly?: boolean | undefined;
  variant?: "canvas" | "simple";
}

export function CanvasRegexTextArea({
  value,
  onChange,
  isReadOnly: isReadOnlyProp,
  variant = "canvas",
}: CanvasRegexTextAreaProps) {
  const isCustomNodeEditor = use(CustomNodeEditorContext);
  const isReadOnly = useIsReadonlyEditor() || isReadOnlyProp;
  const colorizedRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const colorizerId = `${id}-regex-colorizer`;
  const colorizedRegexHtml = useMemo(() => {
    return colorizePattern(value, {
      flags: "u",
    });
  }, [value]);
  const TextAreaComponent = variant === "canvas" ? CanvasTextArea : Textarea;

  return (
    <div
      className={cn(
        "flex-1 min-w-0",
        "font-mono text-base md:text-sm",
        "flex flex-col gap-2",
        "relative",
      )}
    >
      <SimpleTooltip message="A regular expression pattern to match against.">
        <TextAreaComponent
          minRows={1}
          aria-label="Regex"
          value={value}
          placeholder="Regex"
          className="min-h-[32px] text-wrap break-all"
          onValueChange={onChange}
          onChange={(e) => {
            try {
              new RegExp(e.target.value);
              e.target.setCustomValidity(""); // Clear any previous error message
            } catch {
              if (!isCustomNodeEditor) {
                e.target.setCustomValidity(
                  "Please enter a valid regex pattern.",
                );
              }
            }

            if (
              e.target.value.startsWith("|") ||
              e.target.value.endsWith("|")
            ) {
              e.target.setCustomValidity(
                "Regex cannot start or end with a pipe. This will match all text.",
              );
            }
          }}
          onScroll={(e) => {
            if (e.target instanceof HTMLInputElement && colorizedRef.current) {
              colorizedRef.current.style.marginLeft = `-${e.target.scrollLeft}px`;
            }
          }}
        />
      </SimpleTooltip>
      {!isReadOnly && (
        <div
          className={cn(
            "mx-3 py-2 text-wrap break-all",
            "h-full border border-transparent",
            "absolute inset-0 top-0",
            "overflow-hidden pointer-events-none",
          )}
        >
          <div ref={colorizedRef} className={cn("h-full w-full")}>
            <div
              id={colorizerId}
              className="regex-highlight"
              // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
              dangerouslySetInnerHTML={{ __html: colorizedRegexHtml }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
